import type {
  QueryFunctionContext,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groq from 'groq';
import {
  ASSET,
  CAMPAIGN_BLOCKS,
  CLOUDINARY_VIDEO,
  PROMO_CODE,
  SLUG,
} from 'lib/queries';
import { client } from 'lib/sanity';
import type { PartnerPage } from 'types/cms/pages/PartnerPage';

type PartnerPageQuery = UseQueryResult & {
  data: PartnerPage;
};

export const PARTNER_PAGE_KEY = 'partnerPage';

type PartnerPageQueryKey = [string, string];

export const fetchPartner = async ({
  queryKey,
}: QueryFunctionContext<PartnerPageQueryKey>) => {
  const query = groq`
    *[
      (_type == 'timeline_partner' && slug.current == $slug) ||
      (_type == 'timeline_person' && slug.current == $slug && defined(quote))
    ][0] {
      ${SLUG},
      seo {
        title,
        "description": coalesce(hero.quote->text, quote),
        "image": coalesce(hero.media[0], image),
      },
      title,
      "quote": coalesce(hero.quote->text, quote),
      "name": coalesce(hero.quote->attribution->name, name),
      "role": coalesce(hero.quote->attribution->role, role),
      "overline": coalesce(hero.overline { type, text }, overline { type, text }),
      "image": coalesce(hero.media[0] {
        _type == 'altImage' => {
          ${ASSET},
          "palette": {
            "dominantBg": asset->metadata.palette.dominant.background
          },
          hotspot,
          crop
        }
      }, image {
        ${ASSET},
        "palette": {
          "dominantBg": asset->metadata.palette.dominant.background
        },
      }),
      "video": hero.media[0] {
        _type == 'video' => {
          ${CLOUDINARY_VIDEO}
        }
      },
      "cta": coalesce(hero.cta { type }, cta { type }),
      ${PROMO_CODE},
      "blocks": coalesce(template -> blocks[] { ${CAMPAIGN_BLOCKS} }, blocks[]{ ${CAMPAIGN_BLOCKS} })
    }
  `;

  const partner: PartnerPage = await client.fetch(query, { slug: queryKey[1] });

  return partner;
};

export const usePartner = (slug: string) =>
  useQuery({
    queryKey: [PARTNER_PAGE_KEY, slug],
    queryFn: fetchPartner,
  }) as PartnerPageQuery;
